import * as Sentry from '@sentry/react';
import { DateTime } from '@reynolds/datetime';
import { OpType } from 'src/models';
import { IAppointmentState } from 'src/store/states';

type ServiceInfo = {
    readonly title: string;
    readonly category: string;
    readonly msrp: string;
};

type SdEventName =
    | 'scheduleServiceImpression'
    | 'scheduleServiceStart'
    | 'scheduleServiceLogin'
    | 'scheduleServiceVehicleShown'
    | 'scheduleServiceVehicleStart'
    | 'scheduleServiceVehicleIndicated'
    | 'scheduleServiceSelectionShown'
    | 'scheduleServiceSelectionStart'
    | 'scheduleServiceSelectionFinish'
    | 'scheduleServiceTransportShown'
    | 'scheduleServiceTransportStart'
    | 'scheduleServiceTransportFinish'
    | 'scheduleServiceApptShown'
    | 'scheduleServiceApptStart'
    | 'scheduleServiceApptSet'
    | 'scheduleServiceFormShown'
    | 'scheduleServiceFormStart'
    | 'scheduleServiceFormFinish'
    | 'scheduleServiceReviewShown'
    | 'scheduleServiceReviewStart'
    | 'scheduleServiceReviewFinish'
    | 'scheduleServiceAccountCreated';

type SdEventPayloads = {
    scheduleServiceImpression: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
    };
    scheduleServiceStart: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
    };
    scheduleServiceLogin: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
    };
    scheduleServiceVehicleShown: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
    };
    scheduleServiceVehicleStart: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
    };

    scheduleServiceVehicleIndicated: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
    };

    scheduleServiceSelectionShown: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
    };

    scheduleServiceSelectionStart: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
    };

    scheduleServiceSelectionFinish: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceServices: ServiceInfo[];
    };

    scheduleServiceTransportShown: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceTransportType: string;
    };
    scheduleServiceTransportStart: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceTransportType: string;
    };
    scheduleServiceTransportFinish: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceTransportType: string;
    };

    scheduleServiceApptShown: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceServices: ServiceInfo[];
    };

    scheduleServiceApptStart: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceServices: ServiceInfo[];
    };

    scheduleServiceApptSet: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceApptDate: string;
        scheduleServiceApptTime: string;
        scheduleServiceServices: ServiceInfo[];
    };

    scheduleServiceFormShown: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceApptDate: string;
        scheduleServiceApptTime: string;
    };

    scheduleServiceFormStart: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceApptDate: string;
        scheduleServiceApptTime: string;
        scheduleServiceServices: ServiceInfo[];
    };

    scheduleServiceFormFinish: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceApptDate: string;
        scheduleServiceApptTime: string;
        scheduleServiceServices: ServiceInfo[];
    };

    scheduleServiceReviewShown: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceApptDate: string;
        scheduleServiceApptTime: string;
        scheduleServiceServices: ServiceInfo[];
    };
    scheduleServiceReviewStart: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceApptDate: string;
        scheduleServiceApptTime: string;
        scheduleServiceServices: ServiceInfo[];
    };
    scheduleServiceReviewFinish: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceApptDate: string;
        scheduleServiceApptTime: string;
        scheduleServiceServices: ServiceInfo[];
    };

    scheduleServiceAccountCreated: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
    };
};

export const buildApptData = (sessionId: string, providerId: string, appointment: IAppointmentState) => {
    const sdServices = appointment.ApptOps.map((op) => {
        let opCat = '';
        switch (op.opType) {
            case OpType.MaintenanceMenu:
                opCat = 'Scheduled Maintenance';
                break;
            case OpType.Recall:
                opCat = 'Recall';
                break;
            default:
                opCat = 'Repair';
                break;
        }
        let opPrice = '';
        if (op.itemCostHigh && op.itemCostHigh !== '****') {
            opPrice = op.itemCostHigh;
        } else if (op.itemCostLow && op.itemCostLow !== '****') {
            opPrice = op.itemCostLow;
        }

        return {
            title: op.concern || '',
            category: opCat,
            msrp: opPrice,
        };
    });

    const dateValue = new DateTime(new Date(appointment.Appointment.date));
    const time = appointment.Appointment.time.toUpperCase().replace(/^(\d{1,2}:\d{2})(AM|PM)$/, '$1 $2');

    return {
        scheduleServiceSessionId: sessionId,
        scheduleServiceProvider: providerId,
        scheduleServiceApptDate: dateValue.format('YYYY-MM-DD'),
        scheduleServiceApptTime: time,
        scheduleServiceVehicleMake: appointment.ApptVeh.make,
        scheduleServiceVehicleMileage:
            appointment.ApptVeh.mileage === -1 ||
                appointment.ApptVeh.mileage === 0
                ? ''
                : appointment.ApptVeh.mileage,
        scheduleServiceVehicleModel: appointment.ApptVeh.model,
        scheduleServiceVehicleVin: appointment.ApptVeh.vin,
        scheduleServiceVehicleYear: appointment.ApptVeh.vehYr,
        scheduleServiceServices: sdServices,
    };
}

// The start events that should only fire once per session
const ONCE_PER_SESSION_EVENTS = new Set([
    'scheduleServiceStart',
    'scheduleServiceVehicleStart',
    'scheduleServiceSelectionShown',
    'scheduleServiceSelectionStart',
    'scheduleServiceTransportStart',
    'scheduleServiceApptStart',
    'scheduleServiceApptSet',
    'scheduleServiceReviewStart',
    'scheduleServiceFormShown',
    'scheduleServiceFormStart',
    'scheduleServiceFormFinish',
]);

function isOncePerSessionEvent(event: string): boolean {
    return ONCE_PER_SESSION_EVENTS.has(event);
}

export function getProviderId(
    specData,
    toyotaMCLaunched,
    toyotaTDMCLaunched,
    lexusMCLaunched,
    lexusTDMCLaunched
) {
    let providerId = '';

    if (specData?.toyotaTrackingFlag! === 'Y') {
        providerId =
            toyotaMCLaunched || toyotaTDMCLaunched
                ? 'SMARTPATHREYNOLDS'
                : 'REYNOLDS';
    } else if (specData?.lexusTrackingFlag! === 'Y') {
        providerId =
            lexusMCLaunched || lexusTDMCLaunched
                ? 'MONOGRAMREYNOLDS'
                : 'REYNOLDS';
    } else if (
        specData?.mbzTrackingFlag! === 'Y' ||
        specData?.mazdaCATrackingFlag! === 'Y' ||
        specData?.vwTrackingFlag! === 'Y'
    ) {
        providerId = 'REYNOLDS';
    }

    return providerId;
}


export const sendSdEvent = <K extends SdEventName>(event: K, data: SdEventPayloads[K]) => {
    if (!navigator.cookieEnabled) return;

    // Single-fire session events logic
    if (isOncePerSessionEvent(event)) {
        if (typeof sessionStorage !== "undefined") {
            const key = `_sd_event_${event}`;
            if (sessionStorage.getItem(key)) return;
            sessionStorage.setItem(key, '1');
        }
    }

    try {
        const payload = {
            ...data,
            events: event,
        };
        if (typeof window !== "undefined" && typeof window['sd'] === "function") {
            window['sd']('dataLayer', payload);
            window['sd']('send');
        }
    } catch (e) {
        Sentry.captureException(e);
    }
};
